import React, { useState, useMemo } from "react";
import Modal from "../common/Modal";
import {
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import FilePreview from "../common/FilePreview";
import PausedBadge from "../common/PausedBadge";
import { AssignmentResult } from "../../schemas/playlistSchema";
import { playlistService } from "../../api/services/playlistService";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "../../constants";
import { getFullMediaUrl } from "../../utils/mediaUtils";
import { isSameOrder } from "../../utils/playlistOrder";

interface PlaylistPreviewModalProps {
  isOpen: boolean;
  playlist: any;
  assignments?: AssignmentResult[];
  onClose: () => void;
  onDeleteCreative: (assignmentId: string) => void;
  onReorder?: (assignmentIds: string[]) => void | Promise<void>;
}

const DraggablePreviewRow: React.FC<{
  assignmentId: string;
  index: number;
  className: string;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void;
  children: React.ReactNode;
}> = ({
  assignmentId,
  index,
  className,
  onMove,
  onDragStart,
  onDragEnd,
  onClick,
  children,
}) => {
  const ref = React.useRef<HTMLTableRowElement>(null);
  const onDragStartRef = React.useRef(onDragStart);
  const onDragEndRef = React.useRef(onDragEnd);
  onDragStartRef.current = onDragStart;
  onDragEndRef.current = onDragEnd;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.PLAYLIST_ITEM,
      item: () => {
        onDragStartRef.current();
        return { assignmentId, index };
      },
      end: () => {
        onDragEndRef.current();
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [assignmentId, index],
  );

  const [, drop] = useDrop({
    accept: ItemTypes.PLAYLIST_ITEM,
    hover: (draggedItem: { assignmentId: string; index: number }) => {
      if (draggedItem.index !== index) {
        onMove(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      className={`${className} ${isDragging ? "opacity-50" : ""}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `0:${m}:${s}`;
}

const PlaylistPreviewModal: React.FC<PlaylistPreviewModalProps> = ({
  isOpen,
  playlist,
  assignments,
  onClose,
  onDeleteCreative,
  onReorder,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<{ [id: string]: boolean }>({});
  const [subPlaylistAssignments, setSubPlaylistAssignments] = useState<{
    [subPlaylistId: string]: AssignmentResult[];
  }>({});

  const rawAssignments = useMemo(() => {
    if (!playlist) return [];
    return assignments || playlist.media || [];
  }, [playlist, assignments]);

  const [orderedAssignments, setOrderedAssignments] = useState<
    AssignmentResult[]
  >([]);
  const orderedAssignmentsRef = React.useRef<AssignmentResult[]>([]);
  const isDraggingRef = React.useRef(false);
  const initialOrderRef = React.useRef<string[]>([]);
  const onReorderRef = React.useRef(onReorder);
  React.useEffect(() => {
    onReorderRef.current = onReorder;
  }, [onReorder]);

  React.useEffect(() => {
    const sorted = [...rawAssignments].sort((a, b) => {
      const orderA = a.playOrder ?? 0;
      const orderB = b.playOrder ?? 0;
      return orderA - orderB;
    });
    if (!isDraggingRef.current) {
      setOrderedAssignments(sorted);
      orderedAssignmentsRef.current = sorted;
    }
  }, [rawAssignments]);

  React.useEffect(() => {
    if (!isDraggingRef.current) {
      orderedAssignmentsRef.current = orderedAssignments;
    }
  }, [orderedAssignments]);

  const moveAssignment = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (!isDraggingRef.current) return;
      setOrderedAssignments((prev) => {
        const next = [...prev];
        const [removed] = next.splice(dragIndex, 1);
        next.splice(hoverIndex, 0, removed);
        orderedAssignmentsRef.current = next;
        return next;
      });
    },
    [],
  );

  const handleDragStart = React.useCallback(() => {
    isDraggingRef.current = true;
    initialOrderRef.current = orderedAssignmentsRef.current.map((a) => a.id);
  }, []);

  const handleDragEnd = React.useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    const finalOrder = orderedAssignmentsRef.current.map((a) => a.id);
    const initialOrder = initialOrderRef.current;
    initialOrderRef.current = [];

    // Dropping an item back where it started is not a reorder — skip the
    // request so it does not report a change that never happened.
    if (isSameOrder(initialOrder, finalOrder)) return;

    if (finalOrder.length > 0 && onReorderRef.current) {
      void onReorderRef.current(finalOrder);
    }
  }, []);

  const uniqueAssignmentsCount = orderedAssignments.length;

  React.useEffect(() => {
    if (orderedAssignments.length > 0 && !selectedItemId) {
      setSelectedItemId(orderedAssignments[0].id);
    } else if (orderedAssignments.length === 0) {
      setSelectedItemId(null);
    }
  }, [orderedAssignments.length, selectedItemId]);

  if (!playlist) return null;

  const isMainPlaylist = playlist?.type === "main";

  const totalDuration = orderedAssignments.reduce(
    (acc: number, item: AssignmentResult) => {
      const duration = item.duration || 15;
      const repetitions = item.repetitionCount || 1;
      return acc + duration * repetitions;
    },
    0,
  );

  const getSelectedElement = () => {
    if (!selectedItemId) return null;

    const mainItem = orderedAssignments.find(
      (item) => item.id === selectedItemId,
    );
    if (mainItem) return mainItem;

    for (const subPlaylistId in subPlaylistAssignments) {
      const subCreatives = subPlaylistAssignments[subPlaylistId];
      const creative = subCreatives.find((c) => c.id === selectedItemId);
      if (creative) return creative;
    }

    return null;
  };

  const selectedElement = getSelectedElement();

  const toggleExpand = async (
    expandedStateKey: string,
    actualSubPlaylistId: string,
  ) => {
    const isExpanded = expanded[expandedStateKey];
    const newExpandedState = !isExpanded;

    if (newExpandedState && !subPlaylistAssignments[actualSubPlaylistId]) {
      try {
        const assignments =
          await playlistService.getPlaylistItems(actualSubPlaylistId);
        setSubPlaylistAssignments((prev) => ({
          ...prev,
          [actualSubPlaylistId]: assignments || [],
        }));
      } catch (error) {
        setSubPlaylistAssignments((prev) => ({
          ...prev,
          [actualSubPlaylistId]: [],
        }));
      }
    }

    setExpanded((prev) => ({ ...prev, [expandedStateKey]: newExpandedState }));
  };

  const isSubPlaylistItem = (assignment: AssignmentResult): boolean => {
    if (assignment.fileUrl || assignment.thumbnailUrl) {
      return false;
    }

    if (playlist?.subPlaylists && Array.isArray(playlist.subPlaylists)) {
      const matches = playlist.subPlaylists.some(
        (sub: any) => sub.id === assignment.itemId,
      );
      if (matches) return true;
    }

    return playlist?.type === "main";
  };

  const renderPreviewPanel = () => {
    if (isMainPlaylist) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <img
            src="/assets/images/playlist-icon.jpg"
            alt="Playlist"
            className="h-32 w-32 object-contain rounded-lg"
          />
          <p className="mt-3 text-sm text-gray-500 font-medium">
            Main Playlist
          </p>
        </div>
      );
    }

    if (selectedElement && selectedElement.fileUrl) {
      const fileName = (selectedElement.name || "").toLowerCase();
      const fileUrl = (selectedElement.fileUrl || "").toLowerCase();
      let fileType = "image";

      const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
      const isVideo =
        videoExtensions.some((ext) => fileName.includes(ext)) ||
        videoExtensions.some((ext) => fileUrl.includes(ext)) ||
        fileUrl.includes("video");

      if (isVideo) {
        fileType = "video";
      } else if (fileName.includes(".pdf") || fileUrl.includes("pdf")) {
        fileType = "application/pdf";
      } else if (
        fileName.includes(".jpg") ||
        fileName.includes(".jpeg") ||
        fileName.includes(".png") ||
        fileName.includes(".gif") ||
        fileName.includes(".webp") ||
        fileName.includes(".svg")
      ) {
        fileType = "image";
      }

      return (
        <div className="flex flex-col items-center">
          <FilePreview
            name={(selectedElement.name || "Preview") as string}
            type={fileType}
            url={getFullMediaUrl(selectedElement.fileUrl as string)}
            className="max-h-72 w-full object-contain border border-gray-200 bg-white rounded-lg"
          />
          {selectedElement.name && (
            <p className="mt-2 text-sm text-gray-600 font-medium truncate max-w-full">
              {selectedElement.name}
            </p>
          )}
        </div>
      );
    }

    if (selectedElement) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <img
            src="/assets/images/playlist-icon.jpg"
            alt="Playlist"
            className="h-32 w-32 object-contain rounded-lg opacity-50"
          />
          <p className="mt-3 text-sm text-gray-400">No preview available</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full">
        <img
          src="/assets/images/playlist-icon.jpg"
          alt="Playlist"
          className="h-32 w-32 object-contain rounded-lg opacity-30"
        />
        <p className="mt-3 text-sm text-gray-400">No items in playlist</p>
      </div>
    );
  };

  let runningTime = 0;

  const creativeRows: React.ReactElement[] = [];

  orderedAssignments.forEach((item: AssignmentResult, index: number) => {
    const start = runningTime;
    const itemDuration = item.duration || 15;
    const repetitions = item.repetitionCount || 1;
    const totalItemDuration = itemDuration * repetitions;
    runningTime += totalItemDuration;

    const displayName = item.name || "Unnamed";
    const isSubPlaylist = isSubPlaylistItem(item);
    const actualSubPlaylistId = item.itemId;
    const expandedStateKey = item.id;
    const isExpanded = expanded[expandedStateKey];
    const subPlaylistCreatives =
      subPlaylistAssignments[actualSubPlaylistId] || [];
    const isSelected = selectedItemId === item.id;

    creativeRows.push(
      <DraggablePreviewRow
        key={item.id}
        assignmentId={item.id}
        index={index}
        onMove={moveAssignment}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={
          (item.isPaused ? "opacity-50 " : "") +
          (isSelected
            ? "bg-gray-300 cursor-pointer"
            : isSubPlaylist
              ? "bg-gray-200 cursor-pointer hover:bg-gray-250"
              : "cursor-pointer hover:bg-gray-100")
        }
        onClick={(e) => {
          if (isSubPlaylist) {
            e.stopPropagation();
            toggleExpand(expandedStateKey, actualSubPlaylistId);
          } else {
            setSelectedItemId(item.id);
          }
        }}
      >
        <td className="px-4 py-2 align-middle">
          {isSubPlaylist ? (
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
              )}
              <span className="font-semibold">{displayName}</span>
              <span className="ml-2 text-xs text-gray-500">(Sub Playlist)</span>
            </div>
          ) : (
            <span className="inline-flex items-center gap-2">
              {displayName}
              {item.isPaused && <PausedBadge />}
            </span>
          )}
        </td>
        <td className="px-4 py-2 align-middle font-mono">
          {formatTime(start)}
        </td>
        <td className="px-4 py-2 align-middle font-mono">
          {formatTime(start + totalItemDuration)}
        </td>
        <td className="px-4 py-2 align-middle text-center">
          {repetitions > 1 && (
            <span className="text-xs text-gray-500 mr-2">
              &times;{repetitions}
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteCreative(item.id);
            }}
            title="Delete"
            className="hover:bg-red-100 rounded p-1"
          >
            <TrashIcon className="h-5 w-5 text-danger-default transition-colors duration-150" />
          </button>
        </td>
      </DraggablePreviewRow>,
    );

    if (isSubPlaylist && isExpanded) {
      if (subPlaylistCreatives.length === 0) {
        creativeRows.push(
          <tr key={`${expandedStateKey}-loading`} className="bg-gray-50">
            <td
              colSpan={4}
              className="px-4 py-2 align-middle pl-8 text-sm text-gray-500"
            >
              Loading creatives...
            </td>
          </tr>,
        );
      } else {
        let subRunningTime = start;
        subPlaylistCreatives.forEach((creative: AssignmentResult) => {
          const creativeRepetitions = creative.repetitionCount || 1;
          const creativeDuration = creative.duration || 15;
          const creativeStart = subRunningTime;
          subRunningTime += creativeDuration * creativeRepetitions;
          const isCreativeSelected = selectedItemId === creative.id;

          creativeRows.push(
            <tr
              key={`${expandedStateKey}-creative-${creative.id}`}
              className={
                isCreativeSelected
                  ? "bg-gray-200 cursor-pointer"
                  : "bg-gray-50 hover:bg-gray-100 cursor-pointer"
              }
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItemId(creative.id);
              }}
            >
              <td className="px-4 py-2 align-middle pl-8">
                <span className="text-sm">{creative.name || "Unnamed"}</span>
              </td>
              <td className="px-4 py-2 align-middle font-mono text-sm">
                {formatTime(creativeStart)}
              </td>
              <td className="px-4 py-2 align-middle font-mono text-sm">
                {formatTime(
                  creativeStart + creativeDuration * creativeRepetitions,
                )}
              </td>
              <td className="px-4 py-2 align-middle text-center">
                {creativeRepetitions > 1 && (
                  <span className="text-xs text-gray-500">
                    &times;{creativeRepetitions}
                  </span>
                )}
              </td>
            </tr>,
          );
        });
      }
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Preview" size="full">
      {orderedAssignments.length < 4 && (
        <div className="flex items-center justify-center bg-primary-default text-white font-bold py-2 mb-3 text-sm rounded">
          <span className="mr-2">{"\u26A0\uFE0F"}</span>
          <span>At least 4 creatives should be selected for a screen.</span>
        </div>
      )}

      <div className="flex gap-6" style={{ minHeight: "420px" }}>
        {/* Left: Preview Panel */}
        <div className="w-1/3 flex-shrink-0 flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200 p-4">
          {renderPreviewPanel()}
        </div>

        {/* Right: Playlist Details + Table */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="mb-4 pb-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 truncate">
              {playlist.name}
            </h2>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              <span>
                Type:{" "}
                <span className="font-medium text-gray-700 capitalize">
                  {playlist.type}
                </span>
              </span>
              <span>
                Duration:{" "}
                <span className="font-medium text-gray-700">
                  {formatTime(totalDuration)}
                </span>
              </span>
              <span>
                Items:{" "}
                <span className="font-medium text-gray-700">
                  {uniqueAssignmentsCount}
                </span>
              </span>
            </div>
          </div>

          <div className="overflow-auto flex-1">
            <table className="min-w-full border border-gray-300 rounded">
              <thead className="sticky top-0">
                <tr className="bg-primary-default text-white">
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                    Creative
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                    Start Time
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                    End Time
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-bold uppercase w-16">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">{creativeRows}</tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PlaylistPreviewModal;
